<template>
  <div
    ref="jaContentPreview"
    :class="[
      { 'simple-preview-mode': processNum === CREATE_CONTENT_PROCESS_NUM.CONTENT_CONFIRM },
      { 'full-screen-modal': isFullScreenPreviewMode },
      HORIZONTAL_TEMPLATES[templateId]
        ? 'ja-content-horizontal-preview'
        : 'ja-content-vertical-preview',
    ]"
  >
    <template-a
      v-if="templateId === 'A'"
      :processNum="processNum"
      :isFullScreenPreviewMode="isFullScreenPreviewMode"
      :isVisible="isVisible"
      :contentMaterialList="contentMaterialList"
      :clickMaterial="clickMaterial"
      :contentCaption="contentCaption"
      :contentCaptionColor="contentCaptionColor"
      :contentFrame="contentFrame"
      :timestamp="timestamp"
      :isEditing="isEditing"
      :isAudioButtonVisible="isAudioButtonVisible"
    />
    <template-b
      v-if="templateId === 'B'"
      :processNum="processNum"
      :isFullScreenPreviewMode="isFullScreenPreviewMode"
      :isVisible="isVisible"
      :contentMaterialList="contentMaterialList"
      :clickMaterial="clickMaterial"
      :contentCaption="contentCaption"
      :contentCaptionColor="contentCaptionColor"
      :contentFrame="contentFrame"
      :timestamp="timestamp"
      :isEditing="isEditing"
      :isAudioButtonVisible="isAudioButtonVisible"
    />
    <template-c
      v-if="templateId === 'C'"
      :processNum="processNum"
      :isFullScreenPreviewMode="isFullScreenPreviewMode"
      :isVisible="isVisible"
      :contentMaterialList="contentMaterialList"
      :clickMaterial="clickMaterial"
      :contentCaption="contentCaption"
      :contentCaptionColor="contentCaptionColor"
      :contentFrame="contentFrame"
      :timestamp="timestamp"
      :isEditing="isEditing"
      :isAudioButtonVisible="isAudioButtonVisible"
    />
    <template-1
      v-if="templateId === '1'"
      :processNum="processNum"
      :isFullScreenPreviewMode="isFullScreenPreviewMode"
      :isVisible="isVisible"
      :contentMaterialList="contentMaterialList"
      :clickMaterial="clickMaterial"
      :contentCaption="contentCaption"
      :contentCaptionColor="contentCaptionColor"
      :contentFrame="contentFrame"
      :timestamp="timestamp"
      :isEditing="isEditing"
      :isAudioButtonVisible="isAudioButtonVisible"
    />
    <template-2
      v-if="templateId === '2'"
      :processNum="processNum"
      :isFullScreenPreviewMode="isFullScreenPreviewMode"
      :isVisible="isVisible"
      :contentMaterialList="contentMaterialList"
      :clickMaterial="clickMaterial"
      :contentCaption="contentCaption"
      :contentCaptionColor="contentCaptionColor"
      :contentFrame="contentFrame"
      :timestamp="timestamp"
      :isEditing="isEditing"
      :isAudioButtonVisible="isAudioButtonVisible"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import TemplateA from '@/components/Template/HorizontalTemplates/TemplateA.vue';
import TemplateB from '@/components/Template/HorizontalTemplates/TemplateB.vue';
import TemplateC from '@/components/Template/HorizontalTemplates/TemplateC.vue';
import Template1 from '@/components/Template/VerticalTemplates/Template1.vue';
import Template2 from '@/components/Template/VerticalTemplates/Template2.vue';
import { CREATE_CONTENT_PROCESS_NUM, HORIZONTAL_TEMPLATES } from '@/composable/constant_common';

defineProps({
  processNum: Number,
  isFullScreenPreviewMode: { type: Boolean, default: false },
  isVisible: { type: Boolean, default: false },
  templateId: String,
  contentMaterialList: Array,
  contentCaption: String,
  contentCaptionColor: String,
  contentFrame: { type: Object, default: () => ({}) },
  clickMaterial: Function,
  isEditing: Boolean,
  isAudioButtonVisible: { type: Boolean, default: false },
  timestamp: { type: Number, default: 0 },
});

const jaContentPreview = ref(null);

// Resize handler
const handleResize = () => {
  const captionElement = document.querySelector('.full-screen-modal p');
  const fullScreenModalElement = document.querySelector('.full-screen-modal');

  if (captionElement === null) return;

  // テロップは画面横幅分パディングが設定されているため、テロップ横幅からパディング分を引く
  const captionWidth = captionElement.clientWidth - fullScreenModalElement.clientWidth;
  const animationDuration = 10 * (1 + captionWidth / fullScreenModalElement.clientWidth);
  captionElement.style.animation = `caption ${animationDuration}s linear infinite`;
};

// Lifecycle hooks
onMounted(() => {
  const observer = new IntersectionObserver(handleResize);
  observer.observe(jaContentPreview.value);
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped>
/* 横型テンプレートプレビュー */
.ja-content-horizontal-preview {
  width: 40%;
  height: 50%;
}

/* 縦型テンプレートプレビュー */
.ja-content-vertical-preview {
  width: 18%;
  height: 50%;
}

.simple-preview-mode.ja-content-vertical-preview {
  width: min(25vh * 9 / 16, 100vw);
  height: min(25vh, 100vw * 16 / 9);
  margin: auto auto 1rem;
}

.simple-preview-mode.ja-content-horizontal-preview {
  width: min(25vh * 16 / 9, 100vw);
  height: min(25vh, 100vw * 9 / 16);
  margin: auto auto 1rem;
}

/* 縦長テンプレートプレビュー(9:16未満) */
.full-screen-modal.ja-content-vertical-preview {
  height: calc(100vw * 16 / 9);
  max-height: 100vh;
  max-height: 100dvh;
  width: 100vw;
  margin: auto;
}

/* 縦長テンプレートプレビュー(9:16以上) */
@media (min-aspect-ratio: 9/16) {
  .full-screen-modal.ja-content-vertical-preview {
    height: 100vh;
    height: 100dvh;
    width: calc(100vh * 9 / 16);
    width: calc(100dvh * 9 / 16);
    max-width: 100vw;
    margin: auto;
  }
}

/* 横長テンプレートプレビュー(16:9未満) */
.full-screen-modal.ja-content-horizontal-preview {
  height: calc(100vw * 9 / 16);
  max-height: 100vh;
  max-height: 100dvh;
  width: 100vw;
  margin: auto;
}

/* 横型テンプレートプレビュー(16:9以上) */
@media (min-aspect-ratio: 16/9) {
  .full-screen-modal.ja-content-horizontal-preview {
    height: 100vh;
    height: 100dvh;
    width: calc(100vh * 16 / 9);
    width: calc(100dvh * 16 / 9);
    max-width: 100vw;
  }
}
</style>
<style>
/* 縦向き全画面プレビュー字幕 */
@media screen and (orientation: portrait) {
  .ja-content-horizontal-preview.full-screen-modal p,
  .ja-content-vertical-preview.full-screen-modal p {
    font-size: 3rem;
    animation: caption 10s linear infinite;
  }
}

/* 横向き全画面プレビュー字幕 */
@media screen and (orientation: landscape) {
  .ja-content-horizontal-preview.full-screen-modal p,
  .ja-content-vertical-preview.full-screen-modal p {
    font-size: 5rem;
  }
}

@keyframes caption {
  100% {
    transform: translateX(-100%);
  }
}
</style>
